import {IRouteDefinition, IRouteDefinitionParams, IRouteParams, TRouteId} from "../index";
import {getHash, getRouteDefinition} from "../constants/RouteDefinitions";
import {global} from "../appContext";
import {_} from "lodash";
import {
    findShellParentForTabId,
    getShellTabHash,
    getTabIdFromShortHashSegment,
    isPageShellParentRoute,
    isShellContentChildRoute,
    navigateToShellTab,
} from "./pageShellTabRoutes";
import {
    getTabMemoryObservable,
    isShellTabAvailableEarly,
    resolveShellTabId,
} from "./pageShellTabMemory";

export {
    getTabMemoryKey,
    getTabMemoryObservable,
    getTabMemoryStoreId,
    isShellTabAvailableEarly,
    isTabPermissibleEarly,
    resolveShellTabId,
} from "./pageShellTabMemory";

/** Route def for a tab opened inside a PageShell (has `pageShell` set). */
export function isPageShellTabRoute(routeDef: IRouteDefinition): boolean {
    return !!routeDef?.pageShell;
}

/** Parent shell route id for a tab route, if any. */
export function getPageShellParentId(routeDef: IRouteDefinition): TRouteId | null {
    return routeDef?.pageShell ?? null;
}

/**
 * Build route params for mounting PageShell when Crossroads matched a tab child hash.
 */
export function buildPageShellRouteParams(
    tabRouteDef: IRouteDefinition,
    routeParams: IRouteParams,
): IRouteDefinitionParams {
    const shellId = tabRouteDef.pageShell;
    if (!shellId) {
        throw new Error('Tab route is missing pageShell parent id');
    }
    const shellDef = getRouteDefinition(shellId);
    if (!shellDef) {
        throw new Error(`Unknown page shell route: ${shellId}`);
    }
    return {
        ...(routeParams['id'] ? {__id: Number(routeParams['id'])} : {}),
        __routeDef: shellDef,
        __routeId: tabRouteDef.id,
        __routeParams: routeParams,
        __shellRouteDef: shellDef,
        __shellRouteId: shellDef.id,
        __tabRouteDef: tabRouteDef,
    };
}

/** Hash for a tab route id with the given route params. */
export function getTabHash(tabId: TRouteId, params: IRouteParams = {}): string | null {
    const shellParent = findShellParentForTabId(tabId);
    if (shellParent) {
        const hash = getShellTabHash(shellParent, tabId, params, true);
        if (hash) {
            return hash;
        }
    }
    return getHash(tabId, params, true);
}

/**
 * Resolve the active tab id for a PageShell parent route.
 * Priority: available URL/explicit tab → store → tabDefault → first presented.
 * Always falls back to tabDefault / first tab so bare shell entry never no-ops
 * (needed at hasher.init before login/org is ready).
 */
export function resolveActiveTabId(
    shellDef: IRouteDefinition,
    _routeParams: IRouteParams,
    explicitTabId?: TRouteId,
): TRouteId | null {
    const storedTabId = getTabMemoryObservable(shellDef)() as TRouteId | null;
    return resolveShellTabId(shellDef, {
        explicitTabId: explicitTabId ?? null,
        storedTabId,
        isAvailable: (tabId) => isShellTabAvailableEarly(shellDef, tabId),
        requireAvailable: false,
    });
}

/**
 * When a PageShell parent hash ends with /:options:, a tab segment (e.g. personal) is captured as options.
 * Returns that tab id when it matches a configured shell tab (full id or short path segment).
 */
export function getTabIdFromParentSegment(shellDef: IRouteDefinition, routeParams: IRouteParams): TRouteId | null {
    const segment = routeParams?.options;
    if (!segment || typeof segment !== 'string' || !shellDef?.tabs?.length) {
        return null;
    }
    // Full tab id, override, or family-stripped short segment.
    return getTabIdFromShortHashSegment(shellDef, segment);
}

/** Route def for a tab id under a PageShell parent (includes pageShell linkage). */
export function getShellTabRouteDef(shellDef: IRouteDefinition, tabId: TRouteId): IRouteDefinition {
    const tabDef = getRouteDefinition(tabId);
    if (!tabDef) {
        throw new Error(`Unknown tab route: ${tabId}`);
    }
    return _.defaultsDeep({pageShell: shellDef.id}, tabDef);
}

/**
 * True when this shell's canonical hash needs an `{orgnr}` we cannot supply yet.
 * Canonicalizing then would emit `o//…` (empty org) and destroy the deep link before login.
 */
function shellNeedsOrgButMissing(shellDef: IRouteDefinition, routeParams: IRouteParams): boolean {
    if (shellDef.hashPrefixOrgId === false) {
        return false;
    }
    const paramOrg = routeParams?.['orgnr'] ?? routeParams?.['orgid'];
    if (paramOrg != null && String(paramOrg) !== '') {
        return false;
    }
    return !global.login?.selectedOrganizationId?.();
}

/**
 * Navigate to the resolved tab hash for a bare PageShell parent route (replace, not push).
 * @returns true when the URL was rewritten (caller should stop). Returns false — leaving the
 * bare hash untouched — when no tab resolves, or when the org-scoped hash has no `{orgnr}` yet;
 * the caller then activates the default tab in memory so the deep link survives until login/org.
 */
export function redirectToDefaultTab(shellId: TRouteId, routeParams: IRouteParams): boolean {
    const shellDef = getRouteDefinition(shellId);
    if (!shellDef) {
        return false;
    }
    const fromSegment = getTabIdFromParentSegment(shellDef, routeParams);
    const tabId = resolveActiveTabId(shellDef, routeParams, fromSegment ?? undefined);
    if (!tabId) {
        return false;
    }
    if (shellNeedsOrgButMissing(shellDef, routeParams)) {
        return false;
    }
    return navigateToShellTab(shellId, tabId, routeParams, true);
}

export {isPageShellParentRoute};

/** Route id of the PageShell parent for a matched route, if any. */
export function getPageShellParentRouteId(routeDef: IRouteDefinition): TRouteId | null {
    if (routeDef?.pageShell) {
        return routeDef.pageShell;
    }
    if (routeDef?.component?.name === 'component/page-shell/details' && routeDef.tabs?.length) {
        return routeDef.id;
    }
    return null;
}

export function isTabHashForShellParent(parentId: TRouteId, currentRouteId: TRouteId): boolean {
    const tabDef = getRouteDefinition(currentRouteId);
    if (tabDef?.pageShell === parentId) {
        return true;
    }
    const parentDef = getRouteDefinition(parentId);
    if (!parentDef?.tabs?.length) {
        return false;
    }
    return parentDef.tabs.some((tabEntry) => getRouteDefinition(tabEntry)?.id === currentRouteId);
}

/** Whether the mounted route should use PageShell instead of its own component. */
export function shouldMountPageShell(cr: IRouteDefinitionParams): boolean {
    if (cr?.__shellRouteDef) {
        return true;
    }
    const def = cr?.__routeDef;
    return def?.component?.name === 'component/page-shell/details';
}

/** True when the shell mounts one shared content component for all tabs (route-destination panels). */
export function hasPageShellContentWrapper(routeDef: IRouteDefinition | null | undefined): boolean {
    return !!routeDef && 'content' in routeDef && !!routeDef.content;
}

/**
 * Whether tab switches may reuse the same PageShell instance (soft-nav + RouteValidator cache).
 * Content-wrapper shells: always (panels toggle via isCurrent).
 * Per-tab shells: only when bound to a concrete entity (details) — overview shells remount.
 */
export function shouldReusePageShellInstance(
    shellDef: IRouteDefinition | null | undefined,
    entityId: string | number | null | undefined,
): boolean {
    if (!shellDef) {
        return false;
    }
    if (hasPageShellContentWrapper(shellDef)) {
        return true;
    }
    return entityId != null && entityId !== '';
}

const PAGE_SHELL_COMPONENT = 'component/page-shell/details';

/** Resolve the PageShell parent def from mounted route params, if any. */
function getMountedPageShellDef(cr: IRouteDefinitionParams): IRouteDefinition | null {
    const candidates = [cr.__shellRouteDef, cr.__routeDef];
    for (const def of candidates) {
        if (!def) {
            continue;
        }
        if (def.component?.name === PAGE_SHELL_COMPONENT) {
            return def;
        }
        // getRouteDefinition stores a component-stripped copy on component.params.__routeDef;
        // still treat content-wrapper / bare parent shells as mountable PageShells.
        if (hasPageShellContentWrapper(def) || (!!def.tabs?.length && !def.pageShell)) {
            return def;
        }
    }
    return null;
}

/**
 * Stable key for a reusable PageShell instance (shell + entity + org).
 * Returns null when the shell must remount on tab change (per-tab overview, no entity).
 *
 * Org is omitted when hashPrefixOrgId is false (e.g. createcustomer): stray orgnr on
 * routeParams must not break soft-nav and remount the draft form.
 */
export function getPageShellNavigationKey(cr: IRouteDefinitionParams | null | undefined): string | null {
    if (!cr) {
        return null;
    }
    const shellDef = getMountedPageShellDef(cr);
    if (!shellDef) {
        return null;
    }
    const shellId = cr.__shellRouteId ?? shellDef.id;
    if (!shellId) {
        return null;
    }
    const params = cr.__routeParams ?? {};
    const entityId = cr.__id ?? params['id'] ?? params['importfileid'] ?? '';
    if (!shouldReusePageShellInstance(shellDef, entityId)) {
        return null;
    }
    const orgnr = shellDef.hashPrefixOrgId === false
        ? ''
        : (params['orgnr'] ?? params['orgid'] ?? '');
    // Non-tab children get their own key: read ↔ edit must remount the body.
    const tabDef = cr.__tabRouteDef;
    const contentChildId = tabDef && isShellContentChildRoute(shellDef, tabDef) ? tabDef.id : null;
    return contentChildId
        ? `${shellId}|${entityId}|${orgnr}|${contentChildId}`
        : `${shellId}|${entityId}|${orgnr}`;
}

/** @deprecated Prefer getPageShellNavigationKey — same key for all PageShell soft-nav. */
export function getContentShellNavigationKey(
    cr: IRouteDefinitionParams | null | undefined,
): string | null {
    return getPageShellNavigationKey(cr);
}

/**
 * Same reusable PageShell + entity: only the tab segment changed.
 * Soft-nav updates tab state / URL metadata without remounting PageShell.
 * Overview per-tab shells (no content wrapper, no entity) always remount.
 */
export function isSoftPageShellTabNavigation(
    prev: IRouteDefinitionParams | null | undefined,
    next: IRouteDefinitionParams,
): boolean {
    const prevKey = getPageShellNavigationKey(prev);
    const nextKey = getPageShellNavigationKey(next);
    return !!prevKey && !!nextKey && prevKey === nextKey;
}

/** @deprecated Prefer isSoftPageShellTabNavigation. */
export function isSoftContentShellTabNavigation(
    prev: IRouteDefinitionParams | null | undefined,
    next: IRouteDefinitionParams,
): boolean {
    return isSoftPageShellTabNavigation(prev, next);
}
